import React, { useCallback, useEffect, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';

import './style.css';

const slideVariants = {
	enter: direction => ({
		x: direction > 0 ? 300 : -300,
		opacity: 0,
		position: 'absolute',
	}),
	center: {
		x: 0,
		opacity: 1,
		position: 'relative',
	},
	exit: direction => ({
		x: direction > 0 ? -300 : 300,
		opacity: 0,
		position: 'absolute',
	}),
};

const transition = {
	duration: 0.4,
	ease: 'easeInOut',
};

const SWIPE_THRESHOLD = 80; // px

const Carousel = ({
	items,
	renderItem,
	autoPlay = true,
	autoPlayInterval = 5000,
	loop = true,
	showArrows = true,
	showDots = true,
	draggable = true,
	itemsPerPage = 3,
}) => {
	const [carouselState, setCarouselState] = useState({ index: 0, direction: 0 });
	const [isHovered, setIsHovered] = useState(false);
	const [isTransitioning, setIsTransitioning] = useState(false);
	const itemCount = items?.length || 0;

	const paginate = useCallback(
		newDirection => {
			if (!itemCount || isTransitioning) return; //ignore clicks while animating

			setIsTransitioning(true);

			setCarouselState(prev => {
				let newIndex = prev.index + newDirection;

				if (loop) {
					newIndex = (newIndex + itemCount) % itemCount;
				} else {
					if (newIndex < 0) newIndex = 0;
					if (newIndex > itemCount - 1) newIndex = itemCount - 1;
				}

				return { index: newIndex, direction: newDirection };
			});
		},
		[itemCount, loop, isTransitioning]
	);

	const goNext = () => {
		if (!isTransitioning) paginate(1);
	};
	const goPrev = () => {
		if (!isTransitioning) paginate(-1);
	};
	const goTo = targetIndex => {
		if (!itemCount) return;
		const direction = targetIndex > carouselState.index ? 1 : -1;
		setCarouselState({ index: targetIndex, direction });
	};

	// Autoplay
	useEffect(() => {
		if (!autoPlay || isHovered || itemCount <= 1) return;

		const id = setInterval(() => paginate(1), autoPlayInterval);
		return () => clearInterval(id);
	}, [autoPlay, autoPlayInterval, isHovered, paginate, itemCount]);

	// safe current item (avoid accessing items[0] when list is empty)
	const currentItem = itemCount ? items[carouselState.index] : null;

	const visibleItems = (() => {
		if (!itemCount) return [];

		if (itemsPerPage <= 1) {
			return currentItem ? [currentItem] : [];
		}

		const count = Math.min(itemsPerPage, itemCount);

		if (loop) {
			const result = [];
			for (let i = 0; i < count; i++) {
				const idx = (carouselState.index + i) % itemCount;
				result.push(items[idx]);
			}
			return result;
		}

		return items.slice(carouselState.index, Math.min(carouselState.index + count, itemCount));
	})();

	if (!itemCount) return null;

	return (
		<div
			className="Carousel"
			tabIndex={0}
			onKeyDown={e => {
				if (isTransitioning) return;
				if (e.key === 'ArrowRight') {
					e.preventDefault();
					goNext();
				}
				if (e.key === 'ArrowLeft') {
					e.preventDefault();
					goPrev();
				}
			}}
			onMouseEnter={() => setIsHovered(true)}
			onMouseLeave={() => setIsHovered(false)}
		>
			{/* Slider area */}
			<div className="Carousel__viewport">
				<AnimatePresence custom={carouselState.direction} initial={false}>
					<motion.div
						key={currentItem?.id || carouselState.index}
						custom={carouselState.direction}
						variants={slideVariants}
						initial="enter"
						animate="center"
						exit="exit"
						transition={transition}
						drag={draggable && !isTransitioning ? 'x' : false}
						dragConstraints={{ left: 0, right: 0 }}
						dragElastic={0.2}
						onDragEnd={(event, info) => {
							if (info.offset.x < -SWIPE_THRESHOLD) {
								goNext();
							} else if (info.offset.x > SWIPE_THRESHOLD) {
								goPrev();
							}
						}}
						className="Carousel__slide"
						onAnimationComplete={() => setIsTransitioning(false)}
					>
						{renderItem ? (
							<div className="Carousel__items">
								{visibleItems.map((item, i) => {
									const globalIndex = loop ? (carouselState.index + i) % itemCount : carouselState.index + i;

									return (
										<div key={item.id || globalIndex} className="Carousel__item_wrapper">
											{renderItem(item, globalIndex)}
										</div>
									);
								})}
							</div>
						) : (
							<div className="Carousel__items">
								{visibleItems.map((item, i) => (
									<div key={item.id || i} className="Carousel__item_wrapper">
										<div className="Carousel__card">
											<h3 className="Carousel__card__title">{item.title}</h3>
											<p className="Carousel__card__description">{item.description}</p>
										</div>
									</div>
								))}
							</div>
						)}
					</motion.div>
				</AnimatePresence>
			</div>

			{/* Arrows */}
			{showArrows && itemCount > 1 && (
				<>
					<button type="button" onClick={goPrev} disabled={isTransitioning} className="Carousel__arrow Carousel__arrow--left">
						&#x25C0;
					</button>

					<button type="button" onClick={goNext} disabled={isTransitioning} className="Carousel__arrow Carousel__arrow--right">
						&#x25B6;
					</button>
				</>
			)}

			{/* Dots */}
			{showDots && itemCount > 1 && (
				<div className="Carousel__dots">
					{items.map((item, index) => (
						<button
							key={item.id || index}
							onClick={() => goTo(index)}
							className={'Carousel__dot' + (index === carouselState.index ? ' Carousel__dot--active' : '')}
						/>
					))}
				</div>
			)}
		</div>
	);
};
export default Carousel;
